import { cn } from '@/utils/cn'
import { ChatHeader } from './ChatHeader'
import { MessageList } from './MessageList'
import { MessageInput } from './MessageInput'
import type { Message, WidgetActionHandler } from '@/types'

export interface ChatContainerProps {
  title: string
  messages: Message[]
  isLoading?: boolean
  onSendMessage: (message: string) => void
  onWidgetAction?: WidgetActionHandler
  onVote?: (messageId: string, wasHelpful: boolean | null) => void
  isVoting?: boolean
  /** Gates all prompt-pack UI (starter packs + chips). Driven by the
   * backend `suggested_chips_enabled` flag via /config/ui; off by default. */
  promptPacksEnabled?: boolean
  className?: string
}

/**
 * Main chat container component that combines header, message list, and input.
 * Provides skip link and proper document structure for accessibility.
 */
export function ChatContainer({
  title,
  messages,
  isLoading = false,
  onSendMessage,
  onWidgetAction,
  onVote,
  isVoting,
  promptPacksEnabled = false,
  className,
}: ChatContainerProps) {
  return (
    <div
      className={cn(
        'flex h-full flex-col bg-white',
        className
      )}
    >
      {/* Skip link for keyboard navigation */}
      <a
        href="#chat-input"
        className="skip-link"
      >
        Skip to message input
      </a>

      <ChatHeader
        title={title}
      />

      <MessageList
        messages={messages}
        isLoading={isLoading}
        onWidgetAction={onWidgetAction}
        onVote={onVote}
        isVoting={isVoting}
        onSuggestionSelect={onSendMessage}
        promptPacksEnabled={promptPacksEnabled}
        className="flex-1 overflow-hidden"
      />

      <MessageInput
        onSend={onSendMessage}
        disabled={isLoading}
      />
    </div>
  )
}
